import { Button } from '@mui/material';
import { createContext, ReactNode, useContext, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Dialog } from './components/dialog';

type LabelIntlId = 'Save' | 'Cancel' | 'Delete' | 'DiscardUnsavedChanges' | 'CANCEL' | 'LOG OUT';

type ActionModalState = {
  modalOpen: boolean;
  title: ReactNode;
  content: ReactNode;
  requestedAction: () => void;
  requestedActionButtonLabelIntlId?: LabelIntlId;
  cancelAction?: () => void;
  cancelActionButtonLabelIntlId?: LabelIntlId;
};

const ActionModalContext = createContext<
  | { modalState?: ActionModalState; setModalState: (newModalState: ActionModalState) => void }
  | undefined
>(undefined);

export const ActionModal = ({ children }: { children: ReactNode }) => {
  const [modalState, setModalState] = useState<ActionModalState | undefined>(undefined);

  const closeModal = () => setModalState(undefined);
  return (
    <ActionModalContext.Provider
      value={{
        modalState,
        setModalState: (newModalState: ActionModalState) => setModalState(newModalState)
      }}>
      {children}
      {modalState?.modalOpen && (
        <Dialog
          open={modalState.modalOpen !== undefined}
          title={modalState.title}
          content={modalState.content}
          actions={
            <>
              <Button
                onClick={() => {
                  modalState.requestedAction && modalState.requestedAction();
                  closeModal();
                }}>
                <FormattedMessage id={modalState.requestedActionButtonLabelIntlId} />
              </Button>
              <Button
                onClick={() => {
                  modalState.cancelAction && modalState.cancelAction();
                  closeModal();
                }}>
                <FormattedMessage id={modalState.cancelActionButtonLabelIntlId} />
              </Button>
            </>
          }
        />
      )}
    </ActionModalContext.Provider>
  );
};

export const useActionModal = () => {
  const context = useContext(ActionModalContext);
  if (context === undefined) {
    throw new Error('useActionModal must be used within a ActionModalProvider');
  }

  const openActionModal = (
    title: ReactNode,
    content: ReactNode,
    requestedActionButtonLabelIntlId: LabelIntlId,
    cancelActionButtonLabelIntlId: LabelIntlId,
    requestedAction: () => void,
    cancelAction?: () => void
  ) => {
    context.setModalState({
      modalOpen: true,
      title,
      content,
      requestedAction,
      requestedActionButtonLabelIntlId,
      cancelAction,
      cancelActionButtonLabelIntlId
    });
  };

  return {
    openActionModal
  };
};
